import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Modal from "./Modal";

const currency = (n) =>
  new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" }).format(Number(n || 0));

const getDepth = (code) => Math.max(0, String(code || "").split(".").length - 1);

export default function AccountsView({
  accounts,
  loading,
  expandedSet,
  isAllExpanded,
  childrenByParent,
  hasChildren,
  toggleNode,
  toggleExpandAll,
  menuAnchorEl,
  openMenuFor,
  closeMenu,
  selectedAccount,
  canDeleteSelected,
  openAddDialog,
  openEditDialog,
  openDeleteDialog,
  editorOpen,
  editorMode,
  formCode,
  formName,
  setFormName,
  formType,
  setFormType,
  formBalance,
  setFormBalance,
  balanceDisabled,
  typeDisabled,
  isEditorTargetParent,
  closeEditor,
  saveEditor,
  deleteOpen,
  closeDelete,
  confirmDelete,
  snackbarOpen,
  snackbarType,
  snackbarMessage,
  closeSnackbar,
  displayBalanceById,
  types,
  typesLoading,
  typeModalOpen,
  openTypeModal,
  closeTypeModal,
  onSelectType,
}) {
  const [balanceTouched, setBalanceTouched] = useState(false);

  const displayBalance = (id, fallback = 0) => {
    if (!displayBalanceById) return fallback;
    if (displayBalanceById.has(id)) return displayBalanceById.get(id);
    return fallback;
  };

  const renderRows = (parentId = null) => {
    const key = parentId ?? "__root__";
    const items = childrenByParent.get(key) || [];
    const rows = [];

    for (const a of items) {
      const depth = getDepth(a.code);
      const canExpand = hasChildren(a.id);
      const expanded = expandedSet.has(a.id);

      rows.push(
        <TableRow key={a.id} hover onClick={(e) => openMenuFor(e, a.id)} sx={{ cursor: "pointer" }}>
          <TableCell sx={{ width: 56, borderRight: "1px solid #e6e6e6" }}>
            {canExpand ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(a.id);
                }}
                sx={{ borderRadius: 1 }}
              >
                {expanded ? <RemoveIcon /> : <AddIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 40 }} />
            )}
          </TableCell>

          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: depth * 22 }} />
              <Typography sx={{ fontWeight: canExpand ? 900 : 500, color: "#111", display: "inline" }}>
                {a.code} {a.name}
              </Typography>
            </Box>
          </TableCell>

          <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
            {currency(displayBalance(a.id, a.balance))}
          </TableCell>
        </TableRow>
      );

      if (canExpand && expanded) rows.push(...renderRows(a.id));
    }

    return rows;
  };

  const helperBalanceText = useMemo(() => {
    if (editorMode !== "edit") return "";
    if (!selectedAccount) return "";
    if (hasChildren(selectedAccount.id)) return "Este saldo se calcula automáticamente (suma de subcuentas).";
    return "";
  }, [editorMode, selectedAccount, hasChildren]);

  const typeLabel = useMemo(() => {
    const v = String(formType || "");
    if (!v) return "";
    const t = (types || []).find((x) => String(x.value) === v);
    return t ? String(t.label || t.value) : v;
  }, [formType, types]);

  const typeRequired = editorMode === "add" || (!typeDisabled && !isEditorTargetParent);

  return (
    <>
      <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" mb={2}>
            Plan de Cuentas
          </Typography>

          <TableContainer sx={{ maxHeight: 560, border: "1px solid #e6e6e6", borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56, backgroundColor: "#f2f2f2" }}>
                    <Tooltip
                      title={isAllExpanded ? "Minimizar: vuelve al primer nivel" : "Expandir: ver todas las cuentas"}
                      arrow
                    >
                      <IconButton size="small" onClick={toggleExpandAll} sx={{ borderRadius: 1, bgcolor: "white" }}>
                        {isAllExpanded ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 700, textAlign: "center" }}>
                    Cuenta
                  </TableCell>

                  <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 700, textAlign: "right" }}>
                    Saldo
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography sx={{ fontWeight: 800, color: "#222" }}>No hay cuentas para mostrar</Typography>
                        <Typography sx={{ color: "#666", mt: 0.5 }}>
                          Verifica que el backend esté enviando el plan de cuentas.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  renderRows(null)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.18)",
            minWidth: 220,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            if (selectedAccount) openAddDialog(selectedAccount.id);
          }}
          sx={{ gap: 1 }}
        >
          <AddIcon fontSize="small" />
          Agregar
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            if (selectedAccount) openEditDialog(selectedAccount.id);
          }}
          sx={{ gap: 1 }}
        >
          <EditIcon fontSize="small" />
          Modificar
        </MenuItem>

        <MenuItem
          disabled={!canDeleteSelected}
          onClick={() => {
            closeMenu();
            if (selectedAccount && canDeleteSelected) openDeleteDialog(selectedAccount.id);
          }}
          sx={{ gap: 1 }}
        >
          <DeleteIcon fontSize="small" />
          Eliminar
        </MenuItem>

        <Divider />
      </Menu>

      <Dialog
        open={editorOpen}
        onClose={() => {
          setBalanceTouched(false);
          closeEditor();
        }}
        PaperProps={{ sx: { borderRadius: 2, width: 460 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editorMode === "add" ? "Nueva cuenta" : "Modificar cuenta"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Código" value={formCode} fullWidth disabled />

          <TextField
            label="Nombre de la cuenta"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            fullWidth
            autoFocus
          />

          <TextField
            label="Tipo"
            value={typeLabel}
            fullWidth
            onClick={() => {
              if (!typeDisabled && openTypeModal) openTypeModal();
            }}
            InputProps={{ readOnly: true }}
            disabled={typeDisabled || isEditorTargetParent}
            placeholder={typeRequired ? "Seleccionar tipo…" : "No aplica"}
          />

          <TextField
            label="Saldo"
            value={formBalance}
            onChange={(e) => {
              setBalanceTouched(true);
              setFormBalance(e.target.value);
            }}
            fullWidth
            inputMode="decimal"
            disabled={balanceDisabled}
            helperText={balanceDisabled ? "Este saldo se calcula automáticamente (suma de subcuentas)." : helperBalanceText}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setBalanceTouched(false);
              closeEditor();
            }}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setBalanceTouched(false);
              saveEditor();
            }}
            sx={{ textTransform: "none", backgroundColor: "#1565C0" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={closeDelete} PaperProps={{ sx: { borderRadius: 2, width: 420 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>¿Eliminar cuenta?</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ color: "#444" }}>Se eliminará la cuenta y todas sus subcuentas.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button variant="outlined" onClick={closeDelete} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} sx={{ textTransform: "none" }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={typeModalOpen}
        onClose={closeTypeModal}
        items={types}
        loading={typesLoading}
        title="Seleccionar tipo de cuenta"
        onSelect={(item) => onSelectType(item.value)}
        pageSize={100}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbarType} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
